import type { SupportCase } from "./types";

export interface SupportCaseEnvelope {
  tenantPseudonym: string;
  supportCase: SupportCase;
}

export interface ImprovementCandidate {
  clusterKey: string;
  title: string;
  severity: SupportCase["severity"];
  caseCount: number;
  distinctTenants: number;
  firstSeenAt: string;
  lastSeenAt: string;
  volumeGate: "immediate_human" | "met" | "not_met";
  reproductionGate: "pending";
  readyIdState: "evidence_pending";
}

export function redactSupportText(value: string): string {
  return value
    .replace(/01[016789]-?\d{3,4}-?\d{4}/g, "[전화번호 가림]")
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[이메일 가림]")
    .replace(/\b\d{6}-?[1-4]\d{6}\b/g, "[민감번호 가림]")
    .slice(0, 4000);
}

export function compileImprovementCandidates(
  envelopes: SupportCaseEnvelope[],
): ImprovementCandidate[] {
  const clusters = new Map<string, SupportCaseEnvelope[]>();
  for (const envelope of envelopes) {
    const existing = clusters.get(envelope.supportCase.clusterKey) ?? [];
    existing.push(envelope);
    clusters.set(envelope.supportCase.clusterKey, existing);
  }

  return [...clusters.entries()].map(([clusterKey, cases]) => {
    const ordered = cases.toSorted((a, b) =>
      a.supportCase.createdAt.localeCompare(b.supportCase.createdAt),
    );
    const tenants = new Set(cases.map((item) => item.tenantPseudonym));
    const severity =
      cases
        .map((item) => item.supportCase.severity)
        .toSorted(severityOrder)[0] ?? "P3";
    const firstSeenAt = ordered[0]!.supportCase.createdAt;
    const lastSeenAt = ordered.at(-1)!.supportCase.createdAt;
    const spanDays =
      (Date.parse(lastSeenAt) - Date.parse(firstSeenAt)) / 86_400_000;
    const immediate = severity === "P0" || severity === "P1";
    const volumeMet =
      tenants.size >= 3 || (cases.length >= 5 && spanDays <= 14);
    return {
      clusterKey,
      title: redactSupportText(ordered.at(-1)!.supportCase.subject).slice(
        0,
        120,
      ),
      severity,
      caseCount: cases.length,
      distinctTenants: tenants.size,
      firstSeenAt,
      lastSeenAt,
      volumeGate: immediate ? "immediate_human" : volumeMet ? "met" : "not_met",
      reproductionGate: "pending",
      readyIdState: "evidence_pending",
    };
  });
}

function severityOrder(
  a: SupportCase["severity"],
  b: SupportCase["severity"],
): number {
  return (
    ["P0", "P1", "P2", "P3"].indexOf(a) - ["P0", "P1", "P2", "P3"].indexOf(b)
  );
}
